import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  makeStyles,
  Button,
  CircularProgress,
  TextField,
  Modal,
  Theme,
  createStyles,
} from "@material-ui/core";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  GetAllShopLocalCampaignsForChamberDocument,
  ShopLocalCampaignInput,
  ShopLocalCampaignParticipatingMembersInput,
  useCreateShopLocalCampaignMutation,
  useGetAllChamberMembersQuery,
  UserType,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as yup from "yup";
import useRedirect from "../../common/Hooks/useRedirect";
import HoverToolTip from "../../common/utilities/HoverToolTip";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import ShopLocalCampaignEndDatePicker from "./ShopLocalCampaignEndDatePicker";
import Loader from "../../common/Loader/Loader";
import ChooseParticipatingMembers, {
  ParticipationCodeExplanationText,
} from "./ChooseParticipatingMembers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: "#E1A731",
      minHeight: "100vh",
      fontFamily: "DM Sans, sans-serif",
      overflowX: "hidden" as const,
    },
    header: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "30px 40px",
      gap: "8px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: "20px 16px",
      },
    },
    pageTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: "32px",
      lineHeight: "42px",
      color: "white",
      margin: 0,
      [theme.breakpoints.down("sm")]: {
        fontSize: "22px",
        lineHeight: "30px",
      },
    },
    pageSubtitle: {
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: "23px",
      color: "rgba(255, 255, 255, 0.9)",
      marginTop: "8px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
        lineHeight: "1.55",
      },
    },
    contentContainer: {
      padding: "0 40px 40px 40px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: "0 12px 24px 12px",
      },
    },
    contentCard: {
      background: "#FFFFFF",
      borderRadius: "20px",
      padding: "30px 41px",
      width: "100%",
      minHeight: "600px",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "16px",
        borderRadius: "14px",
        minHeight: 0,
      },
    },
    formFrame: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "40px 51px 49px",
      gap: "10px",
      width: "900px",
      maxWidth: "100%",
      background: "#FFFFFF",
      border: "0.7px solid #888888",
      borderRadius: "31px",
      margin: "0 auto",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "16px",
        width: "100%",
        borderRadius: "16px",
      },
    },
    form: {
      width: "100%",
    },
    fieldContainer: {
      marginBottom: "32px",
      paddingBottom: "20px",
      borderBottom: "1px solid #f0f0f0",
      "&:last-child": {
        borderBottom: "none",
        marginBottom: "0",
        paddingBottom: "0",
      },
    },
    fieldLabelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "6px",
    },
    fieldLabel: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#000000",
      display: "flex",
      alignItems: "center",
      fontFamily: "DM Sans, sans-serif",
    },
    requiredAsterisk: {
      color: "#D32F2F",
      marginLeft: "4px",
      fontWeight: "bold",
    },
    helperText: {
      fontSize: "16px",
      color: "#000000",
      marginBottom: "12px",
      lineHeight: "1.5",
      fontFamily: "DM Sans, sans-serif",
    },
    inputField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#FAFAFA",
        "& fieldset": {
          borderColor: "#E0E0E0",
        },
        "&:hover fieldset": {
          borderColor: "#BDBDBD",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#000000",
          borderWidth: "2px",
        },
        "&.Mui-error fieldset": {
          borderColor: "#E1A731",
        },
      },
      "& .MuiInputBase-input": {
        padding: "14px 16px",
        fontSize: "14px",
      },
      "& .MuiInputBase-inputMultiline": {
        padding: "14px 16px",
      },
    },
    inputFieldError: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#E1A731",
          borderWidth: "2px",
        },
      },
    },
    errorContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: "6px",
    },
    errorBadge: {
      background: "#FCEBE9",
      border: "1px dashed #DD3730",
      borderRadius: "7px",
      padding: "4px 10px",
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "11px",
      lineHeight: "15px",
      color: "#DD3730",
    },
    charCounter: {
      fontSize: "12px",
      color: "#000000",
      textAlign: "right",
      marginTop: "6px",
    },
    previewBox: {
      background: "#FFF9E6",
      border: "1px solid #FFE0B2",
      borderRadius: "10px",
      padding: "20px",
      marginTop: "15px",
    },
    previewTitle: {
      fontSize: "14px",
      fontWeight: "bold",
      color: "#000000",
      marginBottom: "10px",
    },
    previewText: {
      fontSize: "14px",
      color: "#000000",
      lineHeight: "1.5",
    },
    dateFieldsContainer: {
      display: "flex",
      gap: "20px",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    dateFieldWrapper: {
      flex: 1,
    },
    submitButton: {
      background: "#E1A731",
      color: "white",
      borderRadius: "25px",
      padding: "14px 60px",
      fontSize: "16px",
      fontWeight: "bold",
      textTransform: "none",
      marginTop: "30px",
      width: "100%",
      maxWidth: "350px",
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      "&:hover": {
        background: "#c99228",
      },
      "&:disabled": {
        background: "#ccc",
      },
    },
    invalidError: {
      textAlign: "center",
      color: "#DD3730",
      fontSize: "14px",
      marginTop: "15px",
    },
    modal: {
      border: "unset",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    paper: {
      height: "600px",
      borderRadius: "15px",
      textAlign: "center",
      width: "740px",
      maxWidth: "90%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "white",
      padding: "40px",
      position: "relative",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100vh - 24px)",
        height: "auto",
        padding: "24px 18px",
        borderRadius: "14px",
      },
    },
    modalButton: {
      margin: "10px",
      marginTop: "20px",
      width: "230px",
      backgroundColor: "#E1A731",
      color: "white",
      fontWeight: "bold",
      height: "45px",
      borderRadius: "25px",
      "&:hover": {
        backgroundColor: "#c99228",
      },
    },
  })
);

const initialValues: ShopLocalCampaignInput = {
  startDateUtcMilli: 0,
  endDateUtcMilli: 0,
  participatingMembers: [],
  campaignName: "",
  prizeDescription: "",
};

const CreateChamberShopLocalCampaign = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const CHARACTER_LIMIT_DESCRIPTION = 250;

  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState(
    enumStrings.shopLocal.updateChamberShopLocalCampaign.anErrorOccurred
  );
  const [invalidError, setInvalidError] = useState(false);
  const [createShopLocalCampaign] = useCreateShopLocalCampaignMutation();
  const redirectToShopLocalCampaignList = useRedirect(
    RedirectUri.activeChamberShopLocal,
    {
      showToast: true,
    }
  );

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const validationSchema = yup.object({
    campaignName: yup
      .string()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.contestName
      ),
    prizeDescription: yup
      .string()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.contestDescription
      ),
    participatingMembers: yup
      .array()
      .required()
      .min(
        1,
        enumStrings.updateChamberShopLocalCompaignValidation
          .oneParticipationPerMember
      )
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation
          .participantIsRequired
      ),
    startDateUtcMilli: yup
      .number()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.startDateUtcMilli
      )
      .min(
        0,
        enumStrings.updateChamberShopLocalCompaignValidation.startDateUtcMilli
      ),
    endDateUtcMilli: yup
      .number()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.endDateUtcMilli
      )
      .min(
        Date.now(),
        enumStrings.updateChamberShopLocalCompaignValidation.endDateUtcMilli
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: ShopLocalCampaignInput) => {
      setLoader(true);
      try {
        setButtonDisabled(true);

        const shopLocalCampaignCreation = await createShopLocalCampaign({
          variables: {
            input: {
              startDateUtcMilli: input.startDateUtcMilli,
              endDateUtcMilli: input.endDateUtcMilli,
              participatingMembers: input.participatingMembers.map(
                (member) => ({ id: member.id })
              ),
              campaignName: input.campaignName,
              prizeDescription: input.prizeDescription,
            },
          },
          refetchQueries: [
            { query: GetAllShopLocalCampaignsForChamberDocument },
            "getAllShopLocalCampaignsForChamber",
          ],
          awaitRefetchQueries: true,
        });
        const createionResult =
          shopLocalCampaignCreation.data?.createShopLocalCampaign;
        if (createionResult?.shopLocalCampaignCreatedSuccessfully === false) {
          setInvalidError(true);
          setCustomError(
            `${enumStrings.perkPartnerAdmin.unableToCreateLocalContest}`
          );
          setLoader(false);
          setButtonDisabled(false);
          return;
        }

        setLoader(false);
        setCreateModalOpen(true);
      } catch (error) {
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // Scroll to first validation error on submit
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = document.querySelector('.Mui-error, .MuiFormHelperText-root.Mui-error');
      if (firstErrorField) {
        const container = firstErrorField.closest('.MuiFormControl-root') || firstErrorField;
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const createChamberShopLocalCampaignObj = useMemo(() => {
    // @ts-ignore
    return enumStrings.groups[UserType.ChamberAdmin][
      RedirectUri.createShopLocal
    ];
  }, [enumStrings.groups]);

  const { data: allChamberMemberData } = useGetAllChamberMembersQuery({
    variables: { chamberId: chamberInfo?.id || "" },
    skip: !chamberInfo?.id,
    fetchPolicy: "cache-and-network",
  });

  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    if (allChamberMemberData?.getAllChamberMembers) {
      setShowLoader(false);
    }
  }, [allChamberMemberData?.getAllChamberMembers]);

  const handleChamberMembersChange = useCallback(
    (selectedChamberMembers: ShopLocalCampaignParticipatingMembersInput[]) => {
      formik.setFieldValue("participatingMembers", selectedChamberMembers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={classes.root}>
      {/* Success Modal */}
      {createModalOpen && (
        <Modal className={classes.modal} open={createModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between" }}
          >
            <h1 style={{ fontWeight: "bold", color: "#000000" }}>
              {enumStrings.chamberShopLocal.toastText}
            </h1>
            <div
              style={{
                padding: "40px",
                paddingTop: 10,
                paddingBottom: 0,
                textAlign: "left",
                fontSize: "16px",
              }}
            >
              <ParticipationCodeExplanationText />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                className={classes.modalButton}
                onClick={() => {
                  redirectToShopLocalCampaignList();
                }}
                variant="contained"
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div className={classes.header}>
        <h1 className={classes.pageTitle}>
          {createChamberShopLocalCampaignObj.title}
        </h1>
        <p className={classes.pageSubtitle}>
          {createChamberShopLocalCampaignObj.description}
        </p>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.contentCard}>
          {showLoader ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: 20,
              }}
            >
              <Loader />
            </div>
          ) : (
            <div className={classes.formFrame}>
              <form onSubmit={formik.handleSubmit} className={classes.form}>
            {/* Contest Name Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.chamberShopLocal.label.campaignName}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
                <HoverToolTip
                  description={enumStrings.chamberShopLocal.tooltip.campaignName}
                  showIt
                />
              </div>
              <p className={classes.helperText}>
                {enumStrings.chamberShopLocal.tooltip.campaignName}
              </p>
              <TextField
                variant="outlined"
                className={`${classes.inputField} ${
                  formik.touched.campaignName && formik.errors.campaignName
                    ? classes.inputFieldError
                    : ""
                }`}
                placeholder={enumStrings.chamberShopLocal.label.campaignName}
                name="campaignName"
                value={formik.values.campaignName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.campaignName && formik.errors.campaignName && (
                <div className={classes.errorContainer}>
                  <span className={classes.errorBadge}>
                    {formik.errors.campaignName}
                  </span>
                </div>
              )}
            </div>

            {/* Prize Description Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.chamberShopLocal.label.prizeDescription}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
                <HoverToolTip
                  description={
                    enumStrings.chamberShopLocal.tooltip.prizeDescription
                  }
                  showIt
                />
              </div>
              <p className={classes.helperText}>
                {enumStrings.chamberShopLocal.tooltip.prizeDescription}
              </p>
              <TextField
                variant="outlined"
                className={`${classes.inputField} ${
                  formik.touched.prizeDescription &&
                  formik.errors.prizeDescription
                    ? classes.inputFieldError
                    : ""
                }`}
                placeholder={enumStrings.chamberShopLocal.label.prizeDescription}
                multiline
                rows={4}
                name="prizeDescription"
                value={formik.values.prizeDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{
                  maxLength: CHARACTER_LIMIT_DESCRIPTION,
                }}
              />
              <div className={classes.charCounter}>
                {(formik.values.prizeDescription || "").length}/
                {CHARACTER_LIMIT_DESCRIPTION}{" "}
                {enumStrings.shopLocal.createShopLocalCampaign.charRemaining}
              </div>
              {formik.touched.prizeDescription &&
                formik.errors.prizeDescription && (
                  <div className={classes.errorContainer}>
                    <span className={classes.errorBadge}>
                      {formik.errors.prizeDescription}
                    </span>
                  </div>
                )}

              {/* Preview Box */}
              <div className={classes.previewBox}>
                <div className={classes.previewTitle}>
                  {
                    enumStrings.shopLocal.updateChamberShopLocalCampaign
                      .contestPreviewMsg
                  }
                </div>
                <div className={classes.previewText}>
                  {enumStrings.shopLocaldescription} {chamberInfo?.name}{" "}
                  {enumStrings.shopLocaldescription2}{" "}
                  <strong>
                    {formik.values.prizeDescription.length > 0
                      ? formik.values.prizeDescription
                      : enumStrings.shopLocal.createShopLocalCampaign.enterText}
                  </strong>
                </div>
              </div>
            </div>

            {/* Date Fields Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.chamberShopLocal.label.campaignDates ||
                    "Contest Duration"}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText}>
                {enumStrings.chamberShopLocal.tooltip.startDateUtcMilli}
              </p>
              <div className={classes.dateFieldsContainer}>
                <div className={classes.dateFieldWrapper}>
                  <div
                    className={classes.fieldLabel}
                    style={{ marginBottom: "8px", fontSize: "14px" }}
                  >
                    {enumStrings.chamberShopLocal.label.startDate ||
                      "Start Date"}
                    <span className={classes.requiredAsterisk}>*</span>
                  </div>
                  <ShopLocalCampaignEndDatePicker
                    formikErrors={formik.errors.startDateUtcMilli}
                    formikVal={formik.values.startDateUtcMilli}
                    formikSetFieldValue={formik.setFieldValue}
                    startAfterDate={Date.now()}
                    isForStartDate
                    fullWidth
                  />
                </div>
                <div className={classes.dateFieldWrapper}>
                  <div
                    className={classes.fieldLabel}
                    style={{ marginBottom: "8px", fontSize: "14px" }}
                  >
                    {enumStrings.chamberShopLocal.label.endDate || "End Date"}
                    <span className={classes.requiredAsterisk}>*</span>
                  </div>
                  <ShopLocalCampaignEndDatePicker
                    formikErrors={formik.errors.endDateUtcMilli}
                    formikVal={formik.values.endDateUtcMilli}
                    formikSetFieldValue={formik.setFieldValue}
                    disabled={!(formik.values.startDateUtcMilli > 0)}
                    startAfterDate={formik.values.startDateUtcMilli}
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* Participating Members Section */}
            <div className={classes.fieldContainer}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel}>
                  {enumStrings.chamberShopLocal.label.participatingMembers ||
                    "Participating Members"}
                  <span className={classes.requiredAsterisk}>*</span>
                </div>
              </div>
              <p className={classes.helperText}>
                {enumStrings.chamberShopLocal.tooltip.participatingMembers ||
                  "Select the members that will participate in this contest."}
              </p>
              <ChooseParticipatingMembers
                selectedChamberMembers={formik.values.participatingMembers}
                handleChamberMembersChange={handleChamberMembersChange}
                allChamberMemberData={allChamberMemberData}
                formikTouchedAvailableToChamberIds={
                  formik.touched.participatingMembers
                }
                formikErrorsAvailableToChamberIds={
                  formik.errors.participatingMembers
                }
              />
            </div>

            {/* Error Messages */}
            {invalidError && (
              <div className={classes.invalidError}>{customError}</div>
            )}

            {Object.keys(formik?.errors || {}).length > 0 &&
              Object.keys(formik?.touched || {}).length > 0 && (
                <div className={classes.invalidError}>
                  {enumStrings.warningToCreate}
                </div>
              )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className={classes.submitButton}
                  disabled={buttonDisabled || loader}
                >
                  {loader ? (
                    <CircularProgress size={24} style={{ color: "white" }} />
                  ) : (
                    enumStrings.shopLocal.createShopLocalCampaign.create
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateChamberShopLocalCampaign;
